/**
 * Edit signed-in user alias and avatar URL.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getAvatarOptions, updateAlias, updateAvatarUrl } from "../scripts/api";
import PageShell from "../components/ui/PageShell";
import GlassPanel from "../components/ui/GlassPanel";
import Button from "../components/ui/Button";
import Alert from "../components/ui/Alert";
import TextField from "../components/ui/TextField";
import SelectField from "../components/ui/SelectField";
import { useLoadingOverlay } from "../context/LoadingOverlayContext";
import {
  validateAlias,
  validateAvatarUrl,
} from "../scripts/profileValidation";

/** Fallback image when no URL yet (display only; not used for validation). */
const PLACEHOLDER_AVATAR_IMG =
  "https://pax-studios.org/images/avatars/g.png";

export default function EditProfile({ session, loading, refreshSession }) {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoadingOverlay();
  const user = session?.user || null;

  const [activeAvatarOptions, setActiveAvatarOptions] = useState([]);
  const [selectedAvatarUrl, setSelectedAvatarUrl] = useState("");
  const [aliasInput, setAliasInput] = useState("");
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [aliasSaving, setAliasSaving] = useState(false);
  const [avatarOptionsLoading, setAvatarOptionsLoading] = useState(true);
  const [ownedAvatarUrls, setOwnedAvatarUrls] = useState(new Set());
  const [feedback, setFeedback] = useState(null);

  const allowedAvatarUrls = useMemo(
    () => new Set(activeAvatarOptions.map((a) => a.url)),
    [activeAvatarOptions]
  );

  const displayName = user?.alias?.trim()
    ? user.alias.trim()
    : user?.email?.includes("@")
      ? user.email.split("@")[0]
      : user?.email || "Profile";

  useEffect(() => {
    setAliasInput(user?.alias || "");
  }, [user?.alias]);

  useEffect(() => {
    if (!user?.avatar_url || allowedAvatarUrls.size === 0) {
      return;
    }
    if (allowedAvatarUrls.has(user.avatar_url)) {
      setSelectedAvatarUrl(user.avatar_url);
    }
  }, [user?.avatar_url, allowedAvatarUrls]);

  useEffect(() => {
    if (!session?.authenticated || !user?.email) {
      setAvatarOptionsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadAvatarOptions = async () => {
      setAvatarOptionsLoading(true);
      try {
        const data = await getAvatarOptions();
        const owned = new Set(data?.ownedAvatarUrls || []);
        const rows = Array.isArray(data?.avatars) ? data.avatars : [];
        const activeOnly = rows.filter((row) => row.active === true);

        if (!cancelled) {
          setOwnedAvatarUrls(owned);
          setActiveAvatarOptions(activeOnly);
          const urlSet = new Set(activeOnly.map((a) => a.url));
          const preferred =
            user?.avatar_url && urlSet.has(user.avatar_url)
              ? user.avatar_url
              : activeOnly[0]?.url || "";
          setSelectedAvatarUrl(preferred);
        }
      } catch (error) {
        if (!cancelled) {
          setFeedback({
            severity: "error",
            message: error?.message || "Failed to load avatar ownership.",
          });
        }
      } finally {
        if (!cancelled) {
          setAvatarOptionsLoading(false);
        }
      }
    };

    loadAvatarOptions();

    return () => {
      cancelled = true;
    };
  }, [session?.authenticated, user?.email]);

  const aliasValidation = useMemo(
    () => validateAlias(aliasInput),
    [aliasInput]
  );
  const avatarUrlAllowed = useMemo(
    () => validateAvatarUrl(selectedAvatarUrl, allowedAvatarUrls),
    [selectedAvatarUrl, allowedAvatarUrls]
  );

  const avatarSelectOptions = useMemo(() => {
    if (avatarOptionsLoading && activeAvatarOptions.length === 0) {
      return [{ value: "__loading", label: "Loading avatars…", disabled: true }];
    }
    if (!avatarOptionsLoading && activeAvatarOptions.length === 0) {
      return [{ value: "__empty", label: "No avatars available", disabled: true }];
    }
    return activeAvatarOptions.map((option) => ({
      value: option.url,
      label: option.name,
      disabled: !ownedAvatarUrls.has(option.url),
    }));
  }, [
    activeAvatarOptions,
    avatarOptionsLoading,
    ownedAvatarUrls,
  ]);

  const avatarSelectValue = useMemo(() => {
    const opts = avatarSelectOptions;
    const current = opts.find((o) => o.value === selectedAvatarUrl && !o.disabled);
    if (current) {
      return selectedAvatarUrl;
    }
    const firstEnabled = opts.find((o) => !o.disabled);
    return firstEnabled?.value ?? opts[0]?.value ?? "";
  }, [avatarSelectOptions, selectedAvatarUrl]);

  if (loading) {
    return null;
  }

  if (!session?.authenticated || !user) {
    return <Navigate to="/" replace />;
  }

  const selectedAvatarIsOwned = ownedAvatarUrls.has(selectedAvatarUrl);
  const previewAvatarUrl = selectedAvatarUrl || user?.avatar_url || PLACEHOLDER_AVATAR_IMG;

  const handleUpdateAvatar = async () => {
    setFeedback(null);
    if (!avatarUrlAllowed) {
      setFeedback({
        severity: "error",
        message: "Please choose a valid avatar from the list.",
      });
      return;
    }
    setAvatarSaving(true);
    showLoading("Updating avatar…");
    try {
      await updateAvatarUrl({ email: user.email, avatar_url: selectedAvatarUrl });
      await refreshSession?.();
      setFeedback({ severity: "success", message: "Avatar updated successfully." });
    } catch (error) {
      setFeedback({
        severity: "error",
        message: error?.message || "Failed to update avatar.",
      });
    } finally {
      hideLoading();
      setAvatarSaving(false);
    }
  };

  const handleUpdateAlias = async () => {
    setFeedback(null);
    const aliasResult = validateAlias(aliasInput);
    if (!aliasResult.ok) {
      setFeedback({ severity: "error", message: aliasResult.message });
      return;
    }
    setAliasSaving(true);
    showLoading("Updating alias…");
    try {
      await updateAlias({ email: user.email, alias: aliasResult.normalized });
      await refreshSession?.();
      setFeedback({ severity: "success", message: "Alias updated successfully." });
    } catch (error) {
      setFeedback({
        severity: "error",
        message: error?.message || "Failed to update alias.",
      });
    } finally {
      hideLoading();
      setAliasSaving(false);
    }
  };

  return (
    <PageShell>
      <GlassPanel>
        <div className="space-y-6">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <h1 className="text-2xl font-bold text-white">Edit Profile</h1>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/profile")}
              className="min-h-[2.75rem] shrink-0 self-start sm:self-auto"
            >
              Back to profile
            </Button>
          </div>

          {feedback ? <Alert severity={feedback.severity}>{feedback.message}</Alert> : null}

          <hr className="border-white/10" />

          <div
            className={
              "rounded-xl border border-white/10 bg-white/[0.04] p-4 shadow-sm backdrop-blur-sm " +
              "transition-colors duration-200 hover:border-white/15"
            }
          >
            <h2 className="text-base font-bold text-white">Avatar</h2>
            <p className="mt-1 text-sm text-white/55">Choose an owned avatar, then save.</p>
            <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-start">
              <img
                src={previewAvatarUrl}
                alt={displayName}
                className="h-[88px] w-[88px] shrink-0 rounded-full border-2 border-white/15 object-cover shadow-md ring-1 ring-white/10"
              />
              <div className="min-w-0 flex-1 space-y-3">
                <SelectField
                  id="avatar-select"
                  label="Avatar"
                  hideLabel
                  value={avatarSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== "__loading" && v !== "__empty") {
                      setSelectedAvatarUrl(v);
                    }
                  }}
                  options={avatarSelectOptions}
                  showPlaceholder={false}
                  disabled={
                    avatarOptionsLoading ||
                    activeAvatarOptions.length === 0
                  }
                />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    type="button"
                    variant="solid"
                    onClick={handleUpdateAvatar}
                    disabled={
                      avatarSaving ||
                      avatarOptionsLoading ||
                      activeAvatarOptions.length === 0 ||
                      !selectedAvatarIsOwned ||
                      !avatarUrlAllowed
                    }
                    className="min-h-[2.75rem] w-full min-w-[10rem] sm:w-auto"
                  >
                    {avatarSaving ? "Updating…" : "Update avatar"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div
            className={
              "rounded-xl border border-white/10 bg-white/[0.04] p-4 shadow-sm backdrop-blur-sm " +
              "transition-colors duration-200 hover:border-white/15"
            }
          >
            <h2 className="text-base font-bold text-white">Alias</h2>
            <p className="mt-1 text-sm text-white/55">Letters and single spaces only (see rules on save).</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1">
                <TextField
                  id="alias"
                  label="Alias"
                  hideLabel
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  placeholder="Enter your alias"
                  disabled={aliasSaving}
                />
                {!aliasValidation.ok && aliasInput.trim() ? (
                  <p className="mt-1 text-xs text-amber-200/90">{aliasValidation.message}</p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="solid"
                onClick={handleUpdateAlias}
                disabled={aliasSaving || !aliasValidation.ok}
                className="min-h-[2.75rem] w-full min-w-[10rem] shrink-0 sm:w-auto"
              >
                {aliasSaving ? "Updating…" : "Update alias"}
              </Button>
            </div>
          </div>
        </div>
      </GlassPanel>
    </PageShell>
  );
}
